import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { LucideProps } from 'lucide-react';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/cn';

export type MenuItem = {
  key: string;
  label: string;
  icon?: ComponentType<LucideProps>;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
};

type Props = {
  items: MenuItem[];
  align?: 'left' | 'right';
  /** Custom trigger; defaults to a three-dot icon button. */
  trigger?: ReactNode;
  /** aria-label for the default trigger */
  triggerLabel?: string;
};

/** Fixed viewport coordinates for the portalled dropdown. */
type Coords = { top: number; left?: number; right?: number };

export function Menu({ items, align = 'right', trigger, triggerLabel = 'More actions' }: Props) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Anchor the portalled menu to the trigger. Recomputed on open and whenever
  // the page scrolls or resizes so it tracks the trigger instead of drifting.
  useLayoutEffect(() => {
    if (!open) return;
    function place() {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setCoords(
        align === 'right'
          ? { top: rect.bottom + 4, right: window.innerWidth - rect.right }
          : { top: rect.bottom + 4, left: rect.left },
      );
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'inline-flex h-9 items-center justify-center rounded-md transition-colors',
          trigger
            ? 'p-0'
            : 'w-9 text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]',
        )}
      >
        {trigger ?? <MoreHorizontal className="size-4" />}
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ position: 'fixed', top: coords.top, left: coords.left, right: coords.right }}
              className="z-50 min-w-[180px] overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] py-1 shadow-lg"
            >
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    role="menuitem"
                    disabled={item.disabled}
                    onClick={() => {
                      setOpen(false);
                      item.onSelect();
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                      'hover:bg-[var(--color-surface-muted)]',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      item.danger && 'text-[var(--color-danger)]',
                    )}
                  >
                    {Icon ? <Icon className="size-4" /> : null}
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
